import { useEffect } from 'react';
import { useResponsive } from 'hooks';
import { signOut } from 'toolkits/redux/auth';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CardHeader from '@mui/material/CardHeader';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import InstagramIcon from '@mui/icons-material/Instagram';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';

import AllRoles from './AllRoles';
import NavSection from './NavSection';
import UpdateUserSelf from './UpdateUserSelf';

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const drawerWidth = 320;
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');
  const { userInfo, userRoles } = useSelector((state) => state.auth);
  const { lastname, firstname, cellphone, media = [] } = userInfo;
  const avatar = media.find((x) => x.collection_name === 'images');

  const logout = async () => {
    try {
      dispatch(signOut());
      onCloseSidebar();
    } catch (error) {}
  };

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Stack px={0.25} height="100%" overflow="hidden" bgcolor="background.paper">
      <Stack
        p={2}
        rowGap={1}
        alignItems="center"
        justifyContent="center"
        bgcolor="primary.main"
        borderRadius="0 0 16px 16px"
        sx={{
          backgroundSize: '150%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundImage: "url('/assets/svg/bg.svg')",
        }}
      >
        <Avatar
          alt="profile"
          src={avatar?.full_url || '/assets/images/picture.png'}
          sx={{
            width: 120,
            height: 120,
            border: '3px solid',
            borderColor: '#fff',
          }}
        />
        <Card sx={{ width: '100%', bgcolor: 'action.hover' }}>
          <CardHeader
            avatar={
              <Avatar
                sx={{
                  color: 'primary.darker',
                  bgcolor: 'action.hover',
                  width: 28,
                  height: 28,
                }}
              >
                <PersonRoundedIcon fontSize="small" color="inherit" />
              </Avatar>
            }
            title={
              <Typography variant="subtitle1" color="#fff">
                {`${firstname} ${lastname}`}
              </Typography>
            }
            action={
              <Typography variant="caption" color="#fff">
                {cellphone}
              </Typography>
            }
          />
        </Card>
        <AllRoles data={userRoles} />
        <Stack columnGap={1} width="100%" direction="row" alignItems="center" justifyContent="center">
          <UpdateUserSelf />
          {userRoles.length > 1 && (
            <Button sx={{ bgcolor: 'primary.dark', color: '#fff' }} color="inherit" fullWidth>
              تغییر نقش
            </Button>
          )}
        </Stack>
      </Stack>
      <Stack flex={1} overflow="scroll">
        <NavSection />
      </Stack>
      <Stack p={2} rowGap={2} justifyContent="center">
        <Stack direction="row" columnGap={1} alignItems="center" justifyContent="center">
          <IconButton
            target="_blank"
            LinkComponent={Link}
            sx={{ bgcolor: 'action.hover' }}
            href={`https://www.instagram.com/`}
          >
            <InstagramIcon fontSize="small" />
          </IconButton>
          <IconButton
            target="_blank"
            LinkComponent={Link}
            sx={{ bgcolor: 'action.hover' }}
            href={`https://www.telegram.me/`}
          >
            <TelegramIcon fontSize="small" />
          </IconButton>
          <IconButton
            target="_blank"
            LinkComponent={Link}
            sx={{ bgcolor: 'action.hover' }}
            href={`https://www.instagram.com/`}
          >
            <WhatsAppIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Divider />
        <Button fullWidth startIcon={<ExitToAppRoundedIcon />} onClick={logout}>
          خروج از حساب کاربری
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Box
      sx={{
        transition: 'all 0.5s ease',
        width: { xs: 0, lg: drawerWidth },
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            elevation: 0,
            sx: {
              border: 'none',
              direction: 'ltr',
              width: drawerWidth,
              bgcolor: 'transparent',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            elevation: 0,
            sx: {
              border: 'none',
              width: drawerWidth,
              bgcolor: 'transparent',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
